import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// shadcn/ui Button, met de FurniFlats-varianten: default (navy), accent (blush), outline (wit, de standaardknop).
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-[7px] whitespace-nowrap rounded-[10px] text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "border border-primary bg-primary text-primary-foreground hover:bg-navy-800",
        accent: "border border-blush-strong bg-blush text-navy-950 hover:bg-blush-strong",
        outline: "border border-line-strong bg-white text-foreground shadow-[0_1px_2px_rgba(20,28,51,.03)] hover:border-[#c4b3ad] hover:bg-[#fffcfb]",
        ghost: "border border-transparent bg-transparent hover:bg-surface-soft",
        destructive: "border border-[#ebc7c3] bg-[#fffafa] text-tone-red hover:bg-tone-red-soft",
        link: "h-auto border-0 bg-transparent p-0 font-semibold text-navy-800 underline-offset-4 hover:underline"
      },
      size: {
        default: "h-[38px] px-3",
        sm: "h-[31px] rounded-lg px-2.5 text-[11px]",
        icon: "h-[38px] w-[38px] p-0"
      }
    },
    defaultVariants: { variant: "outline", size: "default" }
  }
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, type, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} type={asChild ? undefined : (type ?? "button")} {...props} />;
});
Button.displayName = "Button";

export { Button, buttonVariants };
